import { ChangeDetectionStrategy, Component, DoCheck, effect, input, OnInit, output, signal, inject } from '@angular/core';
import { FileItem, FileLikeObject, FileUploadModule } from 'ng2-file-upload';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { IrisModuleFolderService } from '../../../../services/docs/module-folder.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IrisSubjectI } from '@iris/common/models/IrisSubject';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFileUploadHelper } from '../../helpers/dms-file-upload.helper';
import { AuthFileUploaderFactory } from '@iris/common/modules/file-uploader/auth-file-uploader';
import { IrisLoaderModule } from '@iris/common/modules/loader';

@Component({
  selector: 'iris-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    FileUploadModule,
    IrisLoaderModule,
  ],
  providers: [
    IrisDmsFileUploadHelper,
  ],
})
export class IrisFileUploaderComponent implements OnInit, DoCheck {
  private readonly toastService = inject(ToastService);
  private readonly moduleFolderService = inject(IrisModuleFolderService);
  private readonly translateService = inject(TranslateService);
  private readonly dmsFileUploadHelper = inject(IrisDmsFileUploadHelper);
  private readonly authFileUploaderFactory = inject(AuthFileUploaderFactory);

  readonly folderId = input<string>();
  readonly moduleName = input<IrisModules>();
  readonly subjects = input<IrisSubjectI[]>();
  readonly accept = input<string>();
  readonly disabled = input<boolean>(false);

  readonly fileUploaded = output<IrisDMSFileI[]>();

  readonly uploader = this.authFileUploaderFactory.instance({
    url: '/',
    removeAfterUpload: true,
    isHTML5: true,
    maxFileSize: this.dmsFileUploadHelper.maxFileSizeInBytes(),
  });

  readonly maxFileSize = this.dmsFileUploadHelper.maxFileSizeInMB();
  readonly uploading = signal(false);

  constructor() {
    effect(() => {
      this.refreshUploaderUrl(this.folderId());
    });
  }

  ngOnInit(): void {
    this.refreshUploaderUrl();

    this.uploader.onWhenAddingFileFailed = (_item: FileLikeObject, filter: Record<string, string>) => {
      if (filter.name === 'fileSize') {
        this.toastService.error({ message: this.dmsFileUploadHelper.maxFileSizeText });
      }
    };

    this.uploader.onBeforeUploadItem = () => {
      this.uploading.set(true);
    };

    this.uploader.onErrorItem = (item: FileItem) => {
      this.uploading.set(false);
      this.toastService.error({ message: this.translateService.instant('error.UploadFailed', { fileName: item.file.name }) });
    };

    this.uploader.onSuccessItem = (_item: FileItem, resp: string) => {
      this.uploading.set(false);
      this.fileUploaded.emit(JSON.parse(resp));
    };

    this.uploader.onCompleteItem = () => {
      this.uploading.set(false);
    };
  }

  ngDoCheck(): void {
    if (this.uploader?.queue.length) {
      this.uploader.uploadAll();
    }
  }

  refreshUploaderUrl(folderId?: string): void {
    if (!!this.uploader && !!folderId || !!this.moduleName()) {
      const url = folderId
        ? this.moduleFolderService.getFoldersUploadUrl(folderId)
        : this.moduleFolderService.getModuleFileUploadUrl(this.moduleName(), this.subjects());

      this.uploader.setOptions({ url });
    }
  }
}
